import { Link } from 'react-router-dom'
import { m } from 'motion/react'
import { Button } from '@/components/ui'
import { FugueContactForm } from '@/components/FugueContactForm'
import { useSEO } from '@/hooks/useSEO'
import { useScrollReveal, useStaggeredGrid, useHeroReveal } from '@gjl/motion/css'
import { generateBreadcrumbs, breadcrumbLabels } from '@/components/Breadcrumbs'
import { ArrowRight } from 'lucide-react'
import { IntegrationStatusChip } from '@/components/IntegrationStatusChip'
import { integrations, CATEGORY_ORDER, TIER_COPY } from '@/data/integrations'
import type { IntegrationTier } from '@/data/integrations'

const TIER_ORDER: IntegrationTier[] = ['native', 'connected', 'open']

export default function IntegrationsIndexPage() {
  const breadcrumbs = generateBreadcrumbs('/integrations', breadcrumbLabels)

  useSEO({
    title: 'Integrations',
    description:
      'What Fugue Lab runs natively, what you can connect from your own account, and how to move your data anywhere else. Honest status on every integration.',
    url: '/integrations',
    canonical: '/integrations',
    breadcrumbs,
  })

  const heroRef = useHeroReveal<HTMLElement>(0.2)
  const tiersRef = useStaggeredGrid<HTMLElement>('.tier-card', { stagger: 0.1 })
  const tableRef = useScrollReveal<HTMLElement>('.integration-table')
  const ctaRef = useScrollReveal<HTMLElement>('.cta-content')

  return (
    <div className="mx-auto max-w-6xl space-y-24 px-4 py-16">
      {/* Hero */}
      <section ref={heroRef} className="mx-auto max-w-3xl space-y-6 text-center">
        <h1 className="text-fluid-h1">Keep the tools you already use</h1>
        <p className="text-fluid-h4 text-muted-foreground">
          You already have a mailing list, a payment processor, and a calendar your musicians actually check. You should
          not have to abandon them to run your organization on one system.
        </p>
      </section>

      {/* The three tiers */}
      <section ref={tiersRef} className="space-y-8">
        <div className="mx-auto max-w-2xl space-y-3 text-center">
          <h2 className="text-fluid-h2">Three ways in</h2>
          <p className="text-muted-foreground">
            Every integration falls into one of these. Which one you want usually depends on whether the tool is
            somebody&rsquo;s job.
          </p>
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          {TIER_ORDER.map((tier) => (
            <div
              key={tier}
              className="tier-card border-border/50 from-primary/5 rounded-2xl border bg-linear-to-b to-transparent p-8"
            >
              <h3 className="text-xl font-bold">{TIER_COPY[tier].label}</h3>
              <p className="text-muted-foreground mt-3 text-sm leading-relaxed">{TIER_COPY[tier].blurb}</p>
            </div>
          ))}
        </div>
      </section>

      {/* The honest list */}
      <section ref={tableRef} className="integration-table space-y-8">
        <div className="mx-auto max-w-2xl space-y-3 text-center">
          <h2 className="text-fluid-h2">Where each one stands today</h2>
          <p className="text-muted-foreground">
            We label these honestly, including the ones we have not built. If something you need says &ldquo;on the
            roadmap,&rdquo; tell us, that is how the queue gets ordered.
          </p>
        </div>

        {CATEGORY_ORDER.map((category) => {
          const rows = integrations.filter((i) => i.category === category)
          if (rows.length === 0) return null

          return (
            <div key={category} className="space-y-4">
              <h3 className="text-muted-foreground text-sm font-semibold tracking-wide uppercase">{category}</h3>
              <div className="grid gap-4 md:grid-cols-2">
                {rows.map((integration) => {
                  const card = (
                    <>
                      <div className="flex flex-wrap items-center gap-3">
                        <h4 className="text-lg font-bold">{integration.name}</h4>
                        <IntegrationStatusChip status={integration.status} />
                      </div>
                      <p className="text-muted-foreground mt-2 text-sm leading-relaxed">{integration.summary}</p>
                      {integration.caveat && (
                        <p className="text-muted-foreground/80 mt-2 text-xs leading-relaxed italic">
                          {integration.caveat}
                        </p>
                      )}
                      {integration.detail && (
                        <span className="text-primary mt-3 inline-flex items-center gap-2 text-sm font-medium">
                          How it works <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                        </span>
                      )}
                    </>
                  )

                  return integration.detail ? (
                    <Link
                      key={integration.slug}
                      to={`/integrations/${integration.slug}`}
                      className="border-border/50 hover:border-primary/30 group rounded-2xl border p-6 transition-all duration-300 hover:shadow-lg"
                    >
                      {card}
                    </Link>
                  ) : (
                    <div key={integration.slug} className="border-border/50 rounded-2xl border p-6">
                      {card}
                    </div>
                  )
                })}
              </div>
            </div>
          )
        })}
      </section>

      {/* Request CTA */}
      <section
        ref={ctaRef}
        className="cta-content from-primary/10 via-primary/5 border-primary/20 space-y-6 rounded-2xl border bg-linear-to-br to-transparent p-8 text-center md:p-12"
      >
        <h2 className="text-fluid-h3">Need something that is not on this list?</h2>
        <p className="text-muted-foreground mx-auto max-w-2xl">
          Tell us what your organization runs today. We build integrations against real requests from real customers,
          not against a wishlist, so asking is genuinely how something moves up the queue.
        </p>
        <a href="/book" target="_blank" rel="noopener noreferrer">
          <m.div
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            transition={{ type: 'spring', stiffness: 400, damping: 25 }}
            className="inline-block"
          >
            <Button variant="default" size="lg" className="cursor-pointer px-8 py-6 text-lg">
              Book a Free Call <ArrowRight className="ml-1 h-5 w-5" />
            </Button>
          </m.div>
        </a>
      </section>

      <FugueContactForm />
    </div>
  )
}
